/**
 * ProductShowcase - Shared component for displaying product groups
 * Used by both EditorPickRow and ProductGroupDetailPage
 * 
 * Handles:
 * - Responsive scrollbar logic (long/short text)
 * - Layout calculation with alignItems: stretch
 * - Mobile 4-line text constraint with gradient/scrollbar
 * - Resize handling with throttling + breakpoint listener
 * - Image gallery with navigation arrows and thumbnails
 * - Shopify "Add to Bag" integration for direct purchase
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Heart, ExternalLink, Leaf } from 'lucide-react';
import { useWishlist } from '@/context/WishlistContext';
import { useRegion } from '@/context/RegionContext';
import { useSiteSettings } from '@/context/SiteSettingsContext';
import { toast } from 'sonner';
import { FormattedText, getProductCanonicalUrl } from '@/lib/formatters';
import { resolveImageUrl } from '@/lib/api';
import BlurImage from '@/components/BlurImage';
import ImageWithFallback from '@/components/common/ImageWithFallback';
import CustomScrollbar from '@/components/common/CustomScrollbar';
import ShopifyVariantSelector from '@/components/ShopifyVariantSelector';

// Helper functions
const isVideoUrl = (url) => {
  if (!url) return false;
  const videoExtensions = ['.mp4', '.webm', '.mov', '.avi', '.mkv'];
  return videoExtensions.some(ext => url.toLowerCase().includes(ext));
};

const getMediaTagLabel = (type) => {
  if (type === 'ai-edit') return 'AI-Edit';
  if (type === 'creator') return 'Creator media';
  return null;
};

// Mobile description max height (approx 7 lines)
const MOBILE_MAX_LINES = 7;
const MOBILE_LINE_HEIGHT = 26;
const MOBILE_MAX_HEIGHT = MOBILE_MAX_LINES * MOBILE_LINE_HEIGHT;

/**
 * ProductShowcase Component
 * 
 * @param {Object} props
 * @param {Object} props.group - Product group data
 * @param {Array} props.categories - Categories for title lookup
 * @param {string} props.imagePosition - "left" (always left) or "alternate" (alternates based on isEven)
 * @param {boolean} props.isEven - For alternate mode, determines if image is on left (even) or right (odd)
 * @param {boolean} props.showDetailButton - Show "See Item Details" button
 * @param {boolean} props.showShopButton - Show "Shop on Amazon" button (default: true)
 * @param {boolean} props.isLinkable - Make title/image clickable to detail page
 * @param {React.ReactNode} props.titleExtra - Extra content next to title (e.g., share buttons)
 * @param {boolean} props.showThumbnailsBelowImage - Show thumbnails below image (default: true)
 */
const ProductShowcase = ({ 
  group, 
  categories = [],
  imagePosition = 'left',
  isEven = true,
  showDetailButton = true,
  showShopButton = true,
  isLinkable = true,
  titleExtra = null,
  showThumbnailsBelowImage = true,
  className = ''
}) => {
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [isDescriptionOverflowing, setIsDescriptionOverflowing] = useState(false);
  const [selectedColor, setSelectedColor] = useState(null); // Track selected color for variant images
  
  const canonicalUrl = getProductCanonicalUrl(group);
  
  const descriptionRef = useRef(null);
  const leftColumnRef = useRef(null);
  const rightColumnRef = useRef(null);
  const topSectionRef = useRef(null);
  const bottomSectionRef = useRef(null);
  const rafIdRef = useRef(null);
  
  const { isInWishlist, toggleWishlist } = useWishlist();
  const { region } = useRegion();
  const { isShopifyEnabled } = useSiteSettings();
  const inWishlist = isInWishlist(group.id);
  
  // Determine sales mode - default to affiliate if not set
  const salesMode = group.sales_mode || 'affiliate';
  const isDropshipMode = salesMode === 'dropship';
  const isAffiliateMode = salesMode === 'affiliate';
  
  // Check if product has Shopify Product ID for dropship mode
  const shopifyProductId = group.shopify_product?.product_id || group.shopify_product?.variant_id;
  const hasShopifyLink = isDropshipMode && isShopifyEnabled && shopifyProductId && group.shopify_product?.available !== false;
  
  // Check if product should show affiliate links (only in affiliate mode)
  const showAffiliateButtons = isAffiliateMode;

  // Determine if image should be on left based on imagePosition prop
  const imageOnLeft = imagePosition === 'left' || (imagePosition === 'alternate' && isEven);

  // Calculate layout - determines if scrollbar is needed
  const calculateLayout = useCallback(() => {
    // Cancel any pending calculation
    if (rafIdRef.current) {
      cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = null;
    }
    
    const rightCol = rightColumnRef.current;
    const descEl = descriptionRef.current;
    const leftCol = leftColumnRef.current;
    const topSection = topSectionRef.current;
    const bottomSection = bottomSectionRef.current;
    
    // Below lg breakpoint (1024px) - mobile layout with 4-line max
    if (window.innerWidth < 1024) {
      if (rightCol) rightCol.style.height = '';
      
      if (descEl) {
        // Clear styles first to measure natural height
        descEl.style.maxHeight = '';
        descEl.style.overflowY = '';
        
        // Force reflow
        void descEl.offsetHeight;
        
        // Check if content exceeds 4 lines
        const naturalHeight = descEl.scrollHeight;
        
        if (naturalHeight > MOBILE_MAX_HEIGHT) {
          descEl.style.maxHeight = `${MOBILE_MAX_HEIGHT}px`;
          descEl.style.overflowY = 'auto';
          setIsDescriptionOverflowing(true);
        } else {
          setIsDescriptionOverflowing(false);
        }
      }
      return;
    }
    
    // Missing refs - can't calculate
    if (!leftCol || !rightCol || !topSection || !bottomSection || !descEl) {
      return;
    }
    
    const gridParent = leftCol.parentElement;
    if (!gridParent) return;
    
    // Step 1: Clear all constraints to measure natural heights
    rightCol.style.height = '';
    descEl.style.maxHeight = '';
    descEl.style.overflowY = '';
    gridParent.style.alignItems = 'start';
    
    // Step 2: Force synchronous reflow
    void gridParent.offsetHeight;
    
    // Step 3: Measure natural heights
    const leftColHeight = leftCol.offsetHeight;
    const rightColNaturalHeight = rightCol.offsetHeight;
    const topHeight = topSection.offsetHeight;
    const bottomHeight = bottomSection.offsetHeight;
    
    // Calculate available space for description
    const availableForDesc = leftColHeight - topHeight - bottomHeight - 16;
    
    // Check if right column exceeds left column (needs scrollbar)
    const needsScrollbar = rightColNaturalHeight > leftColHeight + 15;
    
    // Restore grid alignment
    gridParent.style.alignItems = '';
    
    // Step 4: Apply final styles based on result
    if (needsScrollbar && availableForDesc >= 60) {
      rightCol.style.height = `${leftColHeight}px`;
      descEl.style.maxHeight = `${availableForDesc}px`;
      descEl.style.overflowY = 'auto';
      setIsDescriptionOverflowing(true);
    } else {
      // No overflow - ensure all constraints are removed
      rightCol.style.height = '';
      descEl.style.maxHeight = '';
      descEl.style.overflowY = '';
      setIsDescriptionOverflowing(false);
    }
  }, []);

  // Window resize handler - throttled to avoid too many calculations
  useEffect(() => {
    let lastCalcTime = 0;
    const THROTTLE_MS = 50;
    let pendingCalc = null;
    
    const handleResize = () => {
      const now = Date.now();
      clearTimeout(pendingCalc);
      
      if (now - lastCalcTime >= THROTTLE_MS) {
        lastCalcTime = now;
        calculateLayout();
      }
      
      pendingCalc = setTimeout(() => {
        calculateLayout();
      }, 100);
    };
    
    // Initial calculation
    const initialTimer = setTimeout(calculateLayout, 300);
    
    // Listen for resize
    window.addEventListener('resize', handleResize);
    
    // Listen for breakpoint change
    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const handleBreakpointChange = () => {
      setTimeout(calculateLayout, 50);
      setTimeout(calculateLayout, 150);
      setTimeout(calculateLayout, 300);
    };
    mediaQuery.addEventListener('change', handleBreakpointChange);
    
    // Handle image loads
    const leftCol = leftColumnRef.current;
    const handleImageLoad = () => setTimeout(calculateLayout, 100);
    
    const images = leftCol?.querySelectorAll('img') || [];
    images.forEach(img => {
      if (!img.complete) {
        img.addEventListener('load', handleImageLoad);
      }
    });
    
    return () => {
      clearTimeout(initialTimer);
      clearTimeout(pendingCalc);
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);
      window.removeEventListener('resize', handleResize);
      mediaQuery.removeEventListener('change', handleBreakpointChange);
      images.forEach(img => img.removeEventListener('load', handleImageLoad));
    };
  }, [calculateLayout, selectedImageIndex]);

  // Get affiliate link based on region
  const affiliateLink = (() => {
    if (region === 'us' && (group.affiliate_link_us || group.amazon_us_link)) {
      return group.affiliate_link_us || group.amazon_us_link || group.amazon_link;
    }
    if (region === 'es' && group.affiliate_link_es) {
      return group.affiliate_link_es;
    }
    return group.affiliate_link_uk || group.amazon_uk_link || group.amazon_link;
  })();

  // Build combined images array - Group Media images + all variant images
  const buildAllImages = () => {
    const images = [];
    const variantImageIndices = {}; // Maps color name to first image index
    
    // 1. Add Group Media images first
    const groupMainImage = resolveImageUrl(group.main_image || group.image_url);
    if (groupMainImage) {
      images.push({ url: groupMainImage, type: group.image_type || '', source: 'group' });
    }
    
    (group.additional_images || [])
      .filter(img => img && (typeof img === 'string' ? img.trim() : img.url))
      .forEach((img, idx) => {
        images.push({ 
          url: resolveImageUrl(typeof img === 'string' ? img : img.url), 
          type: (group.additional_images_types || [])[idx] || '',
          source: 'group'
        });
      });
    
    // 2. Add variant images (keyed by color)
    const variantImages = group.variant_images || {};
    Object.entries(variantImages).forEach(([colorName, colorImages]) => {
      if (!colorImages) return;
      
      // Track the first image index for this color
      if (colorImages.main_image || (colorImages.additional_images?.length > 0)) {
        variantImageIndices[colorName] = images.length;
      }
      
      if (colorImages.main_image) {
        images.push({ 
          url: resolveImageUrl(colorImages.main_image), 
          type: colorImages.main_image_type || '', 
          source: 'variant',
          colorName 
        });
      }
      
      (colorImages.additional_images || []).forEach((img, idx) => {
        if (img) {
          images.push({ 
            url: resolveImageUrl(img), 
            type: (colorImages.additional_images_types || [])[idx] || '', 
            source: 'variant',
            colorName 
          });
        }
      });
    });
    
    return { images, variantImageIndices };
  };

  const { images: allImages, variantImageIndices } = buildAllImages();

  // Handle color change - jump to that color's first image if it has one
  const handleColorChange = useCallback((colorName) => {
    setSelectedColor(colorName);
    
    // If this color has variant images, jump to the first one
    if (variantImageIndices[colorName] !== undefined) {
      setSelectedImageIndex(variantImageIndices[colorName]);
    }
    // If no images for this color, stay on current image (do nothing)
  }, [variantImageIndices]);

  // Category title helpers
  const getCategoryTitle = (categorySlug) => {
    const category = categories.find(c => c.id === categorySlug || c.slug === categorySlug);
    return category?.title || categorySlug;
  };

  const getSubcategoryTitle = (categorySlug, subcategorySlug) => {
    const category = categories.find(c => c.id === categorySlug || c.slug === categorySlug);
    if (!category) return subcategorySlug?.replace(/-/g, ' ');
    const subcategory = (category.subcategories || []).find(s => s.id === subcategorySlug || s.slug === subcategorySlug);
    return subcategory?.name || subcategorySlug?.replace(/-/g, ' ');
  };

  // Navigation handlers
  const goToPrevImage = () => setSelectedImageIndex(prev => (prev === 0 ? allImages.length - 1 : prev - 1));
  const goToNextImage = () => setSelectedImageIndex(prev => (prev + 1) % allImages.length);

  // Wishlist handler
  const handleWishlistClick = async () => {
    const result = await toggleWishlist(group);
    if (result.requiresAuth) return;
    toast.success(result.added ? 'Added to wishlist' : 'Removed from wishlist');
  };

  // Description text
  const description = group.page_description || group.description;

  // Grid column classes based on image position
  const gridColsClass = imageOnLeft 
    ? 'lg:grid-cols-[280px_1fr]' 
    : 'lg:grid-cols-[1fr_280px]';

  // Image wrapper - linkable or not
  const ImageWrapper = ({ children }) => {
    if (isLinkable) {
      return <Link to={canonicalUrl} className="block">{children}</Link>;
    }
    return <>{children}</>;
  };

  // Title wrapper - linkable or not
  const TitleWrapper = ({ children }) => {
    if (isLinkable) {
      return <Link to={canonicalUrl}>{children}</Link>;
    }
    return <>{children}</>;
  };

  return (
    <div className={`grid grid-cols-1 gap-4 lg:gap-10 ${gridColsClass} ${className}`} style={{ alignItems: 'stretch' }}>
      {/* Images Gallery */}
      <div 
        ref={leftColumnRef} 
        className={`space-y-4 w-full lg:w-[280px] lg:min-w-[280px] mx-auto lg:mx-0 order-2 ${imageOnLeft ? 'lg:order-1' : 'lg:order-2'}`}
      >
        <div className="relative">
          <ImageWrapper>
            <div className="aspect-[9/16] w-full min-w-full rounded-lg overflow-hidden bg-site-bg-alt">
              {isVideoUrl(allImages[selectedImageIndex]?.url) ? (
                <video 
                  src={allImages[selectedImageIndex]?.url} 
                  className="w-full h-full object-cover" 
                  muted 
                  loop 
                  autoPlay 
                  playsInline 
                />
              ) : (
                <BlurImage 
                  src={allImages[selectedImageIndex]?.url} 
                  alt={group.image_alt || group.title} 
                  className={`w-full h-full object-cover ${isLinkable ? 'hover:scale-105 transition-transform duration-500' : ''}`}
                  containerClassName="w-full h-full"
                />
              )}
              {getMediaTagLabel(allImages[selectedImageIndex]?.type) && (
                <div className="absolute bottom-3 right-3 px-2 py-1 bg-white/70 backdrop-blur-sm text-black text-[10px] font-mono uppercase tracking-wider rounded">
                  {getMediaTagLabel(allImages[selectedImageIndex]?.type)}
                </div>
              )}
            </div>
          </ImageWrapper>
          
          {/* Navigation Arrows */}
          {allImages.length > 1 && (
            <>
              <button 
                onClick={goToPrevImage} 
                className="absolute left-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
                data-testid="prev-image-btn"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button 
                onClick={goToNextImage} 
                className="absolute right-2 top-1/2 -translate-y-1/2 p-2 bg-white/90 backdrop-blur-sm rounded-full hover:bg-white transition-colors"
                data-testid="next-image-btn"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </>
          )}
          
          {/* Badges */}
          <div className="absolute top-4 left-4 flex flex-col items-start gap-2">
            {group.is_new && !group.is_sold_out && (
              <span className="badge-new" data-testid="badge-new">New</span>
            )}
            {group.is_sustainable && (
              <span className="inline-flex items-center gap-1 bg-green-600 text-white text-[10px] font-mono uppercase px-2 py-1 rounded-sm" data-testid="badge-sustainable">
                <Leaf className="w-3 h-3 flex-shrink-0" />
                <span className="leading-none translate-y-[0.5px]">Sustainable</span>
              </span>
            )}
          </div>

          {/* Wishlist Button */}
          <button 
            onClick={handleWishlistClick} 
            className={`absolute top-4 right-4 p-2.5 rounded-full transition-all ${inWishlist ? 'bg-red-500 text-white' : 'bg-white/90 backdrop-blur-sm text-site-text hover:bg-white'}`}
            data-testid="wishlist-btn"
          >
            <Heart className={`w-5 h-5 ${inWishlist ? 'fill-current' : ''}`} />
          </button>
        </div>

        {/* Thumbnails */}
        {showThumbnailsBelowImage && allImages.length > 1 && (
          <div className="pt-4 border-t border-site-border">
            <div className="flex gap-2 justify-center">
              {allImages.map((media, idx) => (
                <button 
                  key={idx} 
                  onClick={() => setSelectedImageIndex(idx)} 
                  className={`w-12 h-14 rounded overflow-hidden border-2 transition-all ${selectedImageIndex === idx ? 'border-site-text opacity-100' : 'border-transparent opacity-60 hover:opacity-100'}`}
                  data-testid={`thumbnail-${idx}`}
                >
                  {isVideoUrl(media.url) ? (
                    <video src={media.url} className="w-full h-full object-cover" muted />
                  ) : (
                    <ImageWithFallback 
                      src={media.url} 
                      alt={`View ${idx + 1}`} 
                      className="w-full h-full object-cover" 
                      containerClassName="w-full h-full"
                      loading="lazy" 
                      decoding="async" 
                    />
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Details Column */}
      <div 
        ref={rightColumnRef} 
        className={`flex flex-col w-full order-1 ${imageOnLeft ? 'lg:order-2' : 'lg:order-1'}`}
      >
        {/* Top Section: Category + Title */}
        <div ref={topSectionRef}>
          <div className="flex items-baseline gap-2 mb-3 justify-start">
            <Link 
              to={`/${group.category}`} 
              className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted leading-none hover:text-site-text transition-colors"
            >
              {getCategoryTitle(group.category)}
            </Link>
            {group.subcategory && (
              <>
                <span className="font-mono text-[10px] text-site-text-muted leading-none">•</span>
                <Link 
                  to={`/${group.category}/${group.subcategory}`} 
                  className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted leading-none hover:text-site-text transition-colors"
                >
                  {getSubcategoryTitle(group.category, group.subcategory)}
                </Link>
              </>
            )}
          </div>
          <div className="flex items-center gap-3 mb-4">
            <TitleWrapper>
              <h2 className={`font-heading text-3xl md:text-4xl italic ${isLinkable ? 'hover:text-site-text-muted transition-colors' : ''}`}>
                {group.title}
              </h2>
            </TitleWrapper>
            {titleExtra}
          </div>
        </div>

        {/* Description with Scrollbar */}
        {description && (
          <div className="min-h-0 relative mb-4 flex">
            <div ref={descriptionRef} className="min-h-0 flex-1 overflow-hidden scrollbar-hide pr-1">
              <p className="text-sm text-site-text-muted leading-relaxed">
                <FormattedText>{description}</FormattedText>
              </p>
              {isDescriptionOverflowing && (
                <div className="sticky bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-site-bg to-transparent pointer-events-none" />
              )}
            </div>
            <CustomScrollbar scrollRef={descriptionRef} direction="vertical" show={isDescriptionOverflowing} />
          </div>
        )}

        {/* Bottom Section: Products, Tags, Buttons */}
        <div ref={bottomSectionRef} className={isDescriptionOverflowing ? 'lg:mt-auto' : ''}>
          {/* Products List and Shopify Variant Options */}
          <ProductsAndVariantsSection 
            group={group}
            isDropshipMode={isDropshipMode}
            isAffiliateMode={isAffiliateMode}
            hasShopifyLink={hasShopifyLink}
            shopifyProductId={shopifyProductId}
            region={region}
            showDetailButton={showDetailButton}
            canonicalUrl={canonicalUrl}
            onColorChange={handleColorChange}
          />

          {/* Action Buttons - only for non-Shopify products */}
          {!hasShopifyLink && (
            <ActionButtons 
              group={group}
              showShopButton={showShopButton}
              showDetailButton={showDetailButton}
              showAffiliateButtons={showAffiliateButtons}
              affiliateLink={affiliateLink}
              canonicalUrl={canonicalUrl}
            />
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * ProductsAndVariantsSection - Extracted component for products list and Shopify variants with style tags
 */
const ProductsAndVariantsSection = ({ group, isDropshipMode, isAffiliateMode, hasShopifyLink, shopifyProductId, region, showDetailButton, canonicalUrl, onColorChange }) => {
  // In dropship mode, show all products regardless of Amazon URLs
  // In affiliate mode, only show products that have Amazon URLs for the region
  const visibleProducts = isDropshipMode 
    ? (group.products || []).filter(p => p.name)
    : (group.products || []).filter(p => {
        if (region === 'us') return p.amazon_url_us;
        if (region === 'es') return p.amazon_url_es;
        return p.amazon_url_uk;
      });
  
  if (visibleProducts.length === 0 && !hasShopifyLink) return null;
  
  // Get Amazon link for a product based on region
  const getProductLink = (product) => {
    if (!isAffiliateMode) return null;
    if (region === 'us') return product.amazon_url_us;
    if (region === 'es') return product.amazon_url_es;
    return product.amazon_url_uk;
  };
  
  // Reusable product pills renderer
  const renderProductPills = () => visibleProducts.length > 0 && (
    <div>
      <h3 className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted mb-2">What's in this look:</h3>
      <div className="flex flex-col gap-2">
        {visibleProducts.map((product, idx) => {
          const productLink = getProductLink(product);
          return (
            <div key={idx} className="flex flex-wrap items-center gap-2">
              <span className="text-xs px-2 py-1 bg-site-tag-bg text-site-tag-text rounded-full border border-site-tag-border flex items-center gap-1">
                {product.name}
                {product.tag && (
                  <Link 
                    to={`/search?q=${encodeURIComponent(product.tag)}`} 
                    className="text-[9px] px-1 py-0.5 bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors"
                  >
                    {product.tag}
                  </Link>
                )}
                {productLink && (
                  <a 
                    href={productLink} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="ml-0.5 p-0.5 bg-blue-800 text-white rounded hover:bg-blue-900 transition-colors" 
                    title={`Shop ${product.name} on Amazon`} 
                    data-testid={`product-link-${idx}`}
                  >
                    <ExternalLink className="w-2.5 h-2.5" />
                  </a>
                )}
              </span>
              {product.composition && (
                <span className="text-[10px] text-site-text-muted italic">
                  {product.composition}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
  
  // Reusable style tags renderer
  const renderStyleTags = (wrapInDiv = true) => {
    if (!group.group_tags?.length) return null;
    const content = (
      <>
        <h3 className="font-mono text-[10px] uppercase tracking-widest text-site-text-muted mb-2">Style:</h3>
        <div className="flex flex-wrap gap-2 items-center min-h-[28px]">
          {group.group_tags.map((tag, idx) => (
            <Link 
              key={idx} 
              to={`/search?q=${encodeURIComponent(tag)}`} 
              className="text-xs font-mono px-2.5 py-1 bg-site-bg-alt text-site-text-muted rounded hover:bg-stone-200 hover:text-site-text transition-colors"
            >
              {tag}
            </Link>
          ))}
        </div>
      </>
    );
    return wrapInDiv ? <div>{content}</div> : content;
  };
  
  return (
    <div className="mb-4">
      {hasShopifyLink ? (
        // Shopify products: pass pills and tags to ShopifyVariantSelector for inline display with variants
        <ShopifyVariantSelector
          productId={shopifyProductId}
          productTitle={group.shopify_product?.title || group.title}
          displayPrice={group.shopify_product?.price}
          showDetailButton={showDetailButton}
          detailUrl={canonicalUrl}
          groupId={group.id}
          styleTags={group.group_tags?.length > 0 ? <div className="mb-3">{renderStyleTags(false)}</div> : null}
          productPills={renderProductPills()}
          onColorChange={onColorChange}
          isSoldOut={group.is_sold_out}
        />
      ) : (
        // Affiliate products: show pills and style tags inline
        <div className="flex flex-wrap gap-x-16 gap-y-4 items-end">
          {renderProductPills()}
          {renderStyleTags()}
        </div>
      )}
    </div>
  );
};

/**
 * ActionButtons - Extracted component for affiliate shop/detail buttons
 */
const ActionButtons = ({ group, showShopButton, showDetailButton, showAffiliateButtons, affiliateLink, canonicalUrl }) => {
  if (!showShopButton && !showDetailButton) return null;
  
  const primaryBtnClass = "inline-flex items-center justify-center gap-2 px-5 py-2.5 font-mono text-xs uppercase tracking-widest transition-colors";
  const secondaryBtnClass = `${primaryBtnClass} bg-white border-2 border-site-text text-site-text hover:bg-site-bg-alt`;
  
  return (
    <div className="flex flex-col w-full">
      <div className="flex gap-3">
        {showShopButton && showAffiliateButtons && affiliateLink && (
          group.is_sold_out ? (
            <div 
              className={`${showDetailButton ? 'flex-1' : 'w-full'} ${primaryBtnClass} bg-gray-400 text-white cursor-not-allowed`}
              data-testid={`sold-out-btn-${group.id}`}
            >
              Sold Out
            </div>
          ) : (
            <a 
              href={affiliateLink} 
              target="_blank" 
              rel="noopener noreferrer" 
              className={`${showDetailButton ? 'flex-1' : 'w-full'} ${primaryBtnClass} bg-site-text text-white hover:bg-site-text/90`}
              data-testid={`shop-amazon-btn-${group.id}`}
            >
              {group.shop_button_text || 'Shop On Amazon'}
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
          )
        )}
        {showDetailButton && (
          <Link 
            to={canonicalUrl} 
            className={`flex-1 ${secondaryBtnClass}`}
            data-testid={`see-details-btn-${group.id}`}
          >
            See Item Details
          </Link>
        )}
      </div>
      {showShopButton && showAffiliateButtons && affiliateLink && !group.is_sold_out && (
        <p className="text-[10px] text-gray-400 text-center mt-2">
          As an Amazon Associate, we earn from qualifying purchases.
        </p>
      )}
    </div>
  );
};

export default ProductShowcase;
